/**
 * CreateSpaceModal.jsx
 * Place at: src/components/CreateSpaceModal.jsx
 *
 * Shown when eligible user clicks "Create a Space".
 * Collects: name, description, rules (up to 5), tag, emoji, color, weekly prompt.
 * Calls create_space RPC via spaceQueries.
 */

import { useState, useCallback } from "react";
import { createSpace, checkModeratorEligibility } from "../lib/spaceQueries";
import { TAGS } from "../utils/moderation";

const EMOJI_OPTIONS = ["🏡","💚","🌿","🧠","💙","🌸","⚡","🌊","🔥","✨","🎯","🤝","💪","🌱","🦋"];
const COLOR_OPTIONS = [
  "#6366f1","#8b5cf6","#ec4899","#f43f5e","#f97316",
  "#eab308","#22c55e","#14b8a6","#06b6d4","#3b82f6",
];

export default function CreateSpaceModal({ currentUser, onCreated, onClose }) {
  const [step,          setStep]         = useState("form"); // form | creating | success
  const [eligibility,   setEligibility]  = useState(null);
  const [checkingElig,  setCheckingElig] = useState(false);

  const [name,          setName]         = useState("");
  const [description,   setDescription]  = useState("");
  const [rules,         setRules]        = useState(["", "", ""]);
  const [tag,           setTag]          = useState(null);
  const [emoji,         setEmoji]        = useState("🏡");
  const [color,         setColor]        = useState("#6366f1");
  const [weeklyPrompt,  setWeeklyPrompt] = useState("");
  const [error,         setError]        = useState(null);

  // Check eligibility on mount
  useState(() => {
    if (!currentUser) return;
    setCheckingElig(true);
    checkModeratorEligibility()
      .then(result => setEligibility(result))
      .catch(() => setEligibility({ eligible: false, reason: "Could not check eligibility." }))
      .finally(() => setCheckingElig(false));
  });

  const handleRuleChange = useCallback((index, value) => {
    setRules(prev => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  }, []);

  const addRule = useCallback(() => {
    if (rules.length < 5) setRules(prev => [...prev, ""]);
  }, [rules.length]);

  const removeRule = useCallback((index) => {
    setRules(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleSubmit = useCallback(async () => {
    setError(null);

    if (!name.trim()) return setError("Space name is required.");
    if (name.trim().length < 3) return setError("Name must be at least 3 characters.");
    if (!description.trim()) return setError("Please describe what your space is about.");
    if (description.trim().length < 20) return setError("Description must be at least 20 characters.");

    const cleanRules = rules.filter(r => r.trim().length > 0);

    setStep("creating");
    try {
      const space = await createSpace({
        name:         name.trim(),
        description:  description.trim(),
        rules:        cleanRules,
        tag,
        avatarEmoji:  emoji,
        color,
        weeklyPrompt: weeklyPrompt.trim() || null,
        isPrivate:    false,
      });
      setStep("success");
      onCreated?.(space);
    } catch (err) {
      setStep("form");
      if (err.message?.includes("ineligible:")) {
        setError(err.message.replace("ineligible: ", ""));
      } else if (err.message?.includes("unique")) {
        setError("A space with this name already exists. Try a different name.");
      } else {
        setError("Failed to create space. Please try again.");
      }
    }
  }, [name, description, rules, tag, emoji, color, weeklyPrompt, onCreated]);

  return (
    <div style={{
      position:   "fixed",
      inset:      0,
      background: "rgba(0,0,0,0.5)",
      display:    "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex:     1000,
      padding:    "16px",
    }}
    onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background:   "var(--card-bg-color)",
        borderRadius: 20,
        width:        "100%",
        maxWidth:     560,
        maxHeight:    "90vh",
        overflowY:    "auto",
        boxShadow:    "0 24px 64px rgba(0,0,0,0.2)",
      }}>

        {/* Header */}
        <div style={{
          padding:        "22px 24px 16px",
          borderBottom:   "1px solid var(--border-color)",
          display:        "flex",
          alignItems:     "center",
          justifyContent: "space-between",
          position:       "sticky",
          top:            0,
          background:     "var(--card-bg-color)",
          borderRadius:   "20px 20px 0 0",
          zIndex:         1,
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 700 }}>Create a Space</h3>
            <p style={{ margin: "2px 0 0", fontSize: "0.78rem", color: "var(--text-muted)" }}>
              Build a community around a topic you care about
            </p>
          </div>
          <button onClick={onClose} style={{
            background: "none", border: "none", cursor: "pointer",
            fontSize: 20, color: "var(--text-muted)", padding: "4px 8px",
          }}>✕</button>
        </div>

        {/* Eligibility check */}
        {checkingElig && (
          <div style={{ padding: 32, textAlign: "center", color: "var(--text-muted)" }}>
            Checking eligibility…
          </div>
        )}

        {/* Not eligible */}
        {!checkingElig && eligibility && !eligibility.eligible && (
          <div style={{ padding: 32, textAlign: "center" }}>
            <div style={{ fontSize: "3rem", marginBottom: 12 }}>🔒</div>
            <h4 style={{ margin: "0 0 8px", color: "var(--text-color)" }}>Not eligible yet</h4>
            <p style={{ color: "var(--text-muted)", fontSize: "0.9rem", margin: 0 }}>
              {eligibility.reason}
            </p>
          </div>
        )}

        {/* Success */}
        {step === "success" && (
          <div style={{ padding: 32, textAlign: "center" }}>
            <div style={{ fontSize: "3rem", marginBottom: 12 }}>🎉</div>
            <h4 style={{ margin: "0 0 8px", color: "var(--text-color)" }}>Space Created!</h4>
            <p style={{ color: "var(--text-muted)", fontSize: "0.9rem", margin: "0 0 20px" }}>
              Your space is live. Start inviting members!
            </p>
            <button onClick={onClose} style={{
              padding: "10px 28px", borderRadius: 10, border: "none",
              background: color, color: "#fff", fontWeight: 700,
              cursor: "pointer", fontFamily: "inherit",
            }}>
              Done
            </button>
          </div>
        )}

        {/* Creating */}
        {step === "creating" && (
          <div style={{ padding: 32, textAlign: "center", color: "var(--text-muted)" }}>
            <div style={{ fontSize: "2rem", marginBottom: 12 }}>⏳</div>
            Creating your space…
          </div>
        )}

        {/* Form */}
        {!checkingElig && eligibility?.eligible && step === "form" && (
          <div style={{ padding: "20px 24px 24px" }}>

            {/* Preview badge */}
            <div style={{
              display:      "flex",
              alignItems:   "center",
              gap:          12,
              padding:      "12px 16px",
              background:   `${color}10`,
              border:       `1.5px solid ${color}30`,
              borderRadius: 12,
              marginBottom: 20,
            }}>
              <span style={{ fontSize: 28 }}>{emoji}</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "var(--text-color)" }}>
                  {name || "Your Space Name"}
                </div>
                <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                  🛡️ Moderated by @{currentUser?.username}
                </div>
              </div>
            </div>

            {/* Emoji + Color row */}
            <div style={{ display: "flex", gap: 16, marginBottom: 18 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Space Emoji</label>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                  {EMOJI_OPTIONS.map(e => (
                    <button
                      key={e}
                      onClick={() => setEmoji(e)}
                      style={{
                        width:        34,
                        height:       34,
                        borderRadius: 8,
                        border:       emoji === e ? `2px solid ${color}` : "1.5px solid var(--border-color)",
                        background:   emoji === e ? `${color}15` : "transparent",
                        cursor:       "pointer",
                        fontSize:     18,
                      }}
                    >{e}</button>
                  ))}
                </div>
              </div>
              <div>
                <label style={labelStyle}>Accent Color</label>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6, maxWidth: 120 }}>
                  {COLOR_OPTIONS.map(c => (
                    <button
                      key={c}
                      onClick={() => setColor(c)}
                      style={{
                        width:        26,
                        height:       26,
                        borderRadius: "50%",
                        background:   c,
                        border:       color === c ? "2.5px solid var(--text-color)" : "2px solid transparent",
                        cursor:       "pointer",
                        outline:      color === c ? `2px solid ${c}` : "none",
                        outlineOffset: 2,
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Name */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Space Name *</label>
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Anxiety Support"
                maxLength={50}
                style={inputStyle}
              />
            </div>

            {/* Description */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>What is this space about? *</label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Describe your space — who it's for, what people share here, and what makes it special."
                rows={3}
                maxLength={300}
                style={{ ...inputStyle, resize: "none" }}
              />
              <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", textAlign: "right", marginTop: 2 }}>
                {description.length}/300
              </div>
            </div>

            {/* Topic tag */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Topic Tag</label>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                {TAGS.map(t => (
                  <button
                    key={t.label}
                    onClick={() => setTag(tag === t.label ? null : t.label)}
                    style={{
                      padding:      "4px 12px",
                      borderRadius: 20,
                      border:       tag === t.label ? `1.5px solid ${t.color}` : "1.5px solid var(--border-color)",
                      background:   tag === t.label ? `${t.color}18` : "transparent",
                      color:        tag === t.label ? t.color : "var(--text-muted)",
                      fontSize:     "0.78rem",
                      fontWeight:   600,
                      cursor:       "pointer",
                      fontFamily:   "inherit",
                    }}
                  >
                    {t.emoji} {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Rules */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Space Rules (up to 5)</label>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 6 }}>
                {rules.map((rule, i) => (
                  <div key={i} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <span style={{ fontSize: "0.78rem", color: "var(--text-muted)", width: 20, textAlign: "center", flexShrink: 0 }}>
                      {i + 1}.
                    </span>
                    <input
                      value={rule}
                      onChange={e => handleRuleChange(i, e.target.value)}
                      placeholder={`Rule ${i + 1}…`}
                      maxLength={100}
                      style={{ ...inputStyle, marginBottom: 0, flex: 1 }}
                    />
                    {rules.length > 1 && (
                      <button
                        onClick={() => removeRule(i)}
                        style={{
                          background: "none", border: "none", cursor: "pointer",
                          color: "var(--text-muted)", fontSize: 16, padding: "0 4px", flexShrink: 0,
                        }}
                      >✕</button>
                    )}
                  </div>
                ))}
                {rules.length < 5 && (
                  <button
                    onClick={addRule}
                    style={{
                      padding:    "6px 12px",
                      borderRadius: 8,
                      border:     "1.5px dashed var(--border-color)",
                      background: "transparent",
                      color:      "var(--text-muted)",
                      fontSize:   "0.8rem",
                      cursor:     "pointer",
                      fontFamily: "inherit",
                      alignSelf:  "flex-start",
                    }}
                  >
                    + Add rule
                  </button>
                )}
              </div>
            </div>

            {/* Weekly prompt */}
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Weekly Theme Prompt (optional)</label>
              <input
                value={weeklyPrompt}
                onChange={e => setWeeklyPrompt(e.target.value)}
                placeholder="e.g. What helped you cope this week?"
                maxLength={150}
                style={inputStyle}
              />
              <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: 4 }}>
                This appears on your space card in the main feed to attract members.
              </div>
            </div>

            {/* Error */}
            {error && (
              <div style={{
                padding:      "10px 14px",
                borderRadius: 8,
                background:   "rgba(239,68,68,0.1)",
                border:       "1px solid rgba(239,68,68,0.3)",
                color:        "#ef4444",
                fontSize:     "0.85rem",
                marginBottom: 16,
              }}>
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              onClick={handleSubmit}
              style={{
                width:        "100%",
                padding:      "12px 0",
                borderRadius: 12,
                border:       "none",
                background:   color,
                color:        "#fff",
                fontWeight:   700,
                fontSize:     "0.95rem",
                cursor:       "pointer",
                fontFamily:   "inherit",
                transition:   "opacity 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = "0.9"}
              onMouseLeave={e => e.currentTarget.style.opacity = "1"}
            >
              Create Space ✨
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const labelStyle = {
  fontSize:    "0.78rem",
  fontWeight:  700,
  color:       "var(--text-muted)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  display:     "block",
};

const inputStyle = {
  width:        "100%",
  padding:      "10px 12px",
  borderRadius: 8,
  border:       "1.5px solid var(--border-color)",
  background:   "var(--input-bg, var(--bg-secondary))",
  color:        "var(--text-color)",
  fontSize:     "0.875rem",
  fontFamily:   "inherit",
  outline:      "none",
  boxSizing:    "border-box",
  marginBottom: 0,
};